import { faX } from "@fortawesome/pro-light-svg-icons";
import { faArrowRotateBack } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tab } from "@headlessui/react";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { CustomCheckbox } from "../../../../ui/CustomCheckbox";
import { useMapData } from "../../hooks/mapDataAndFilters";
import { useMap } from "../../hooks/mapState";
import { automaticPanAndZoomAtom, prevMapViewStateAtom } from "../../state";
import { ControlContainer } from "../common";
import { DataPointDetailsTab } from "./ContextInfo/datapoint";
import { EmissionDetailsTab } from "./ContextInfo/emissions";
import { ExtraDataDetailsTab } from "./ContextInfo/extradata";
import { InfrastructureTab, PipelineTab } from "./ContextInfo/infrastructure";
import { UnmatchedDataPointDetailsTab } from "./ContextInfo/unmatchedDatapoint";
import { InsightsData } from "./Insights";
import { SettingsDropdown } from "./Insights/ui";
import { insightsConfigAtom } from "./state";

interface CustomTabButtonProps {
    label: string;
    onClose?: () => void;
}

const CustomTabButton = (props: CustomTabButtonProps) => (
    <Tab className="bg-white rounded px-1 py-0.5 text-sm flex items-center gap-1 border hover:bg-ae-blue-40 ui-selected:border-ae-blue-550 ui-selected:text-ae-blue-550">
        <span className={props.onClose ? "italic" : ""}>{props.label}</span>
        {props.onClose && (
            <button onClick={props.onClose} className="flex items-center">
                <FontAwesomeIcon icon={faX} className="w-3 h-3" />
            </button>
        )}
    </Tab>
);

const InsightsSettings = () => {
    const [insightsConfig, setInsightsConfig] = useAtom(insightsConfigAtom);

    return (
        <SettingsDropdown button="lg">
            <div className="flex items-center gap-2 text-sm mb-1">
                <CustomCheckbox
                    checked={insightsConfig.generalCounts ? "yes" : "no"}
                    onClick={(value) =>
                        setInsightsConfig((i) => ({
                            ...i,
                            generalCounts: value === "yes",
                        }))
                    }
                />
                Summary Information
            </div>
            <div className="flex items-center gap-2 text-sm mb-1">
                <CustomCheckbox
                    checked={insightsConfig.emissionList ? "yes" : "no"}
                    onClick={(value) =>
                        setInsightsConfig((i) => ({
                            ...i,
                            emissionList: value === "yes",
                        }))
                    }
                />
                Emission Insights
            </div>
            <div className="flex items-center gap-2 text-sm mb-1">
                <CustomCheckbox
                    checked={insightsConfig.infraInsights ? "yes" : "no"}
                    onClick={(value) =>
                        setInsightsConfig((i) => ({
                            ...i,
                            infraInsights: value === "yes",
                        }))
                    }
                />
                Infrastructure Insights
            </div>
            <div className="flex items-center gap-2 text-sm mb-1">
                <CustomCheckbox
                    checked={insightsConfig.thirdPartyInsights ? "yes" : "no"}
                    onClick={(value) =>
                        setInsightsConfig((i) => ({
                            ...i,
                            thirdPartyInsights: value === "yes",
                        }))
                    }
                />
                Third Party Insights
            </div>
            <div className="flex items-center gap-2 text-sm mb-1">
                <CustomCheckbox
                    checked={insightsConfig.bridgerInsights ? "yes" : "no"}
                    onClick={(value) =>
                        setInsightsConfig((i) => ({
                            ...i,
                            bridgerInsights: value === "yes",
                        }))
                    }
                />
                Equipment Emission Insights
            </div>
            <div className="flex items-center gap-2 text-sm mb-1">
                <CustomCheckbox
                    checked={insightsConfig.nonDetects ? "yes" : "no"}
                    onClick={(value) =>
                        setInsightsConfig((i) => ({
                            ...i,
                            nonDetects: value === "yes",
                        }))
                    }
                />
                Non Detect Insights
            </div>
        </SettingsDropdown>
    );
};

interface Insights {
    showInsights: boolean;
}

export const Insights = (props: Insights) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const { flyTo } = useMap("mainMap");
    const [autoMove] = useAtom(automaticPanAndZoomAtom);
    const [prevMapState, setPrevMapState] = useAtom(prevMapViewStateAtom);
    const { selectedContext, setSelectedContext } = useMapData("mainMap");

    const somethingPicked =
        selectedContext.siteId ||
        selectedContext.emissionRecordId ||
        selectedContext.dataPointId ||
        selectedContext.unmatchedDataPointId ||
        selectedContext.pipeline ||
        selectedContext.extraDataId;
    const showInsightsPanel = props.showInsights || somethingPicked;

    // Highlight tab when something is picked.
    useEffect(() => {
        setSelectedIndex(somethingPicked ? 1 : 0);
    }, [somethingPicked, showInsightsPanel]);

    if (!showInsightsPanel) {
        return null;
    }

    const moveBack = () => {
        if (autoMove && prevMapState) {
            flyTo(
                prevMapState.latitude,
                prevMapState.longitude,
                prevMapState.zoom,
                true,
            );
            setPrevMapState(null);
        }
    };

    const closeAndMoveBack = () => {
        moveBack();
        setSelectedContext({});
    };

    const availableTabs = [
        props.showInsights && {
            label: "Insights",
            contents: <InsightsData />,
        },
        selectedContext.siteId && {
            label: "Site",
            onClose: closeAndMoveBack,
            contents: <InfrastructureTab />,
        },
        selectedContext.pipeline && {
            label: "Pipeline",
            onClose: closeAndMoveBack,
            contents: <PipelineTab />,
        },
        selectedContext.emissionRecordId && {
            label: "Emission",
            onClose: closeAndMoveBack,
            contents: <EmissionDetailsTab />,
        },
        selectedContext.dataPointId && {
            label: "Public Emission",
            onClose: closeAndMoveBack,
            contents: <DataPointDetailsTab />,
        },
        selectedContext.unmatchedDataPointId && {
            label: "Unmatched Detection",
            onClose: closeAndMoveBack,
            contents: <UnmatchedDataPointDetailsTab />,
        },
        selectedContext.extraDataId && {
            label: "Area Surveys",
            onClose: closeAndMoveBack,
            contents: <ExtraDataDetailsTab />,
        },
    ].filter((i) => !!i);

    return (
        <ControlContainer className="p-0.5 w-fit h-full overflow-auto">
            <div className="flex flex-col gap-1">
                <Tab.Group
                    selectedIndex={selectedIndex}
                    onChange={setSelectedIndex}
                >
                    <div className="flex items-center justify-between">
                        <Tab.List className="flex items-center gap-1">
                            {availableTabs.map((tab) => (
                                <CustomTabButton
                                    key={tab.label}
                                    label={tab.label}
                                    onClose={tab.onClose}
                                />
                            ))}
                        </Tab.List>
                        <div className="flex items-center gap-1">
                            {autoMove && prevMapState && (
                                <button
                                    onClick={moveBack}
                                    className="bg-white rounded px-1 py-0.5 text-sm flex items-center gap-1 border hover:bg-ae-blue-40 ui-selected:border-ae-blue-550 ui-selected:text-ae-blue-550"
                                >
                                    <FontAwesomeIcon
                                        icon={faArrowRotateBack}
                                        className="w-3 h-3"
                                    />
                                    Back to previous view
                                </button>
                            )}
                            {props.showInsights && <InsightsSettings />}
                        </div>
                    </div>
                    <Tab.Panels>
                        {availableTabs.map((tab) => (
                            <Tab.Panel key={tab.label}>
                                {tab.contents}
                            </Tab.Panel>
                        ))}
                    </Tab.Panels>
                </Tab.Group>
            </div>
        </ControlContainer>
    );
};
